import { useState } from "react";
import { Layers, Leaf } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StageBuilder, StageLadder } from "@/components/client/StageBuilder";
import { StageTimeline } from "@/components/client/StageTimeline";

/** Clinician: see the client's stage ladder, draft stages (proposal), suggest moving on (proposal). Never moves the client. */
function StageHeadsupNote({ clientId, habitId, note, onSeen }) {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  const seen = async () => {
    try { await api.post(`/clinician/client/${clientId}/habits/${habitId}/stages/seen`); setHidden(true); onSeen?.(); }
    catch (e) { toast.error("Couldn't save that"); }
  };
  return (
    <div className="mt-2 flex items-center gap-2 text-xs text-stone-500" data-testid={`stage-headsup-${habitId}`}>
      <Leaf className="w-3 h-3 text-stone-400" /> <span>{note.text}</span>
      <button onClick={seen} data-testid={`stage-headsup-seen-${habitId}`} className="font-semibold text-stone-600 underline-offset-2 hover:underline">Seen</button>
    </div>
  );
}

export function ClinicianStageRow({ clientId, habit: h, requested, onSent, headsup }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (payload) => {
    setBusy(true);
    try { await api.post(`/clinician/client/${clientId}/habits/${h.id}/stages/propose`, { ...payload, note }); toast.success("Sent as a proposal — the member decides."); setOpen(false); setNote(""); onSent?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send that"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid={`clinician-stage-row-${h.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-500"><Layers className="w-3 h-3" /> {h.stage ? `Stage ${h.stage.index + 1} of ${h.stage.count}` : "Single measure"}</span>
        {requested && <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid={`stage-request-${h.id}`}>Member asked you to draft stages</span>}
        {!open && <button onClick={() => setOpen(true)} data-testid={`stages-draft-${h.id}`} className="font-semibold text-amber-800 hover:text-amber-900">{h.stage ? "Propose different stages" : "Draft stages for this member"}</button>}
        {!open && h.stage && !h.stage.is_last && <button onClick={() => send({ kind: "move" })} disabled={busy} data-testid={`stages-suggest-move-${h.id}`} className="font-semibold text-emerald-800 hover:text-emerald-900">Suggest moving on</button>}
      </div>
      {h.stage && <div className="mt-1.5"><StageLadder stage={h.stage} testid={`clinician-stage-ladder-${h.id}`} /></div>}
      {h.stage_timeline && <div className="mt-2"><StageTimeline journey={h.stage_timeline} showWeeks testid={`clinician-stage-timeline-${h.id}`} /></div>}
      {headsup && <StageHeadsupNote clientId={clientId} habitId={h.id} note={headsup} onSeen={onSent} />}
      {open && (
        <div className="mt-3 rounded-2xl border border-stone-200 p-3 space-y-3">
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} placeholder="A line for the member (optional)" data-testid={`stages-draft-note-${h.id}`} className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm" />
          <StageBuilder allowAi={false} initial={h.stage?.stages} index={0} kind={h.kind} saveLabel="Send as a proposal" busy={busy} onCancel={() => setOpen(false)} onSave={(stages) => send({ kind: "stages", stages })} />
        </div>
      )}
    </div>
  );
}
